import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { queriesApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  History,
  Search,
  ArrowLeft,
  MapPin,
  Calendar,
  Wifi,
  LogOut,
  Map,
  List,
  Users,
  Settings,
  Check,
  X,
  Filter,
} from "lucide-react";

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function HistoryPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [queries, setQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viabilityFilter, setViabilityFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const isNOC = user?.role === "noc";

  // Load queries
  useEffect(() => {
    const loadQueries = async () => {
      setLoading(true);
      try {
        const response = await queriesApi.getAll({ limit: 500 });
        setQueries(response.data);
        setFilteredQueries(response.data);
      } catch (error) {
        toast.error("Erro ao carregar histórico");
      } finally {
        setLoading(false);
      }
    };
    loadQueries();
  }, []);

  // Filter queries
  useEffect(() => {
    let filtered = queries;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (query) =>
          query.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          query.user_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Viability filter
    if (viabilityFilter === "viable") {
      filtered = filtered.filter((q) => q.is_viable);
    } else if (viabilityFilter === "not_viable") {
      filtered = filtered.filter((q) => !q.is_viable);
    }

    setFilteredQueries(filtered);
  }, [searchTerm, viabilityFilter, queries]);

  // Sidebar for NOC
  const Sidebar = () => (
    <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
            <Wifi className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
            <p className="text-xs text-slate-400">NOC Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? "bg-[#FF4500] text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );

  // Sales header
  const SalesHeader = () => (
    <header className="bg-slate-900 border-b border-slate-800 p-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">TECPLUS</h1>
              <p className="text-xs text-slate-400">Histórico</p>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={logout}
          className="text-slate-400 hover:text-white"
        >
          <LogOut className="w-5 h-5" />
        </Button>
      </div>
    </header>
  );

  const MainContent = () => (
    <main className={`flex-1 p-8 overflow-auto ${!isNOC ? "bg-slate-950" : ""}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-white">Histórico de Consultas</h2>
          <p className="text-slate-400">
            {filteredQueries.length} consulta{filteredQueries.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar por endereço ou usuário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="search-input"
            />
          </div>
          <Select value={viabilityFilter} onValueChange={setViabilityFilter}>
            <SelectTrigger className="w-full sm:w-48 h-12 bg-slate-900 border-slate-700 text-white" data-testid="filter-select">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">Todas</SelectItem>
              <SelectItem value="viable" className="text-white">Viáveis</SelectItem>
              <SelectItem value="not_viable" className="text-white">Não Viáveis</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner" />
              </div>
            ) : filteredQueries.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Nenhuma consulta encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Endereço</TableHead>
                      <TableHead className="text-slate-400">CTO</TableHead>
                      <TableHead className="text-slate-400">Distância</TableHead>
                      {isNOC && <TableHead className="text-slate-400">Usuário</TableHead>}
                      <TableHead className="text-slate-400">Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQueries.map((query) => (
                      <TableRow
                        key={query.id}
                        className="border-slate-800 hover:bg-slate-800/50"
                        data-testid={`history-row-${query.id}`}
                      >
                        <TableCell>
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase ${
                              query.is_viable
                                ? "bg-green-500/20 text-green-400"
                                : "bg-red-500/20 text-red-400"
                            }`}
                          >
                            {query.is_viable ? (
                              <>
                                <Check className="w-3 h-3" />
                                Viável
                              </>
                            ) : (
                              <>
                                <X className="w-3 h-3" />
                                Não Viável
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2 max-w-[300px]">
                            <MapPin className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-300 text-sm line-clamp-2">
                              {query.address}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-slate-300">
                          {query.nearest_cto_name || "-"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`font-mono font-bold ${
                              query.is_viable ? "text-green-400" : "text-red-400"
                            }`}
                          >
                            {query.distance_meters?.toFixed(0)}m
                          </span>
                        </TableCell>
                        {isNOC && (
                          <TableCell className="text-slate-300">{query.user_name}</TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Calendar className="w-4 h-4" />
                            {new Date(query.created_at).toLocaleDateString("pt-BR")}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="history-page">
      {isNOC ? <Sidebar /> : null}
      <div className="flex-1 flex flex-col">
        {!isNOC && <SalesHeader />}
        <MainContent />
      </div>
    </div>
  );
}
